// iter 283: Excel export from the products list with a fixed column set
// (Photo, Item No, Description, Size, Pcs/CTN, Factory Cost, Carton Size,
// CI Inv Qty, Order QTY, Price) and embedded product photos.
import ExcelJS from "exceljs";
import { embedPhoto } from "./excelPhoto";
import { groupByProductId } from "./exportImageUtils";

const PHOTO_COL_WIDTH = 18;
const ROW_HEIGHT_PT = 64;

const COLUMNS = [
  { id: "photo",        label: "Photo",              width: PHOTO_COL_WIDTH, level: "product", isPhoto: true },
  { id: "item_no",      label: "Item No",            width: 16, level: "product", valueFn: (r) => r.item_no || "", bold: true },
  { id: "description",  label: "Description",        width: 44, level: "product", valueFn: (r) => r.description || "", wrap: true },
  { id: "size",         label: "Size",               width: 18, level: "variant", valueFn: (r) => r.size || "" },
  { id: "pcs_ctn",      label: "Pcs/CTN",            width: 10, level: "variant", valueFn: (r) => r.pcs_per_ctn ?? "", align: "right" },
  { id: "factory_cost", label: "Factory Cost (RMB)", width: 16, level: "variant", valueFn: (r) => (r.purchase_price != null ? Number(r.purchase_price) : ""), numFmt: '"¥"#,##0.00', align: "right" },
  { id: "carton_size",  label: "Carton Size (cm)",   width: 18, level: "variant",
    valueFn: (r) => (r.carton_length && r.carton_width && r.carton_height ? `${r.carton_length}\u00d7${r.carton_width}\u00d7${r.carton_height}` : "") },
  { id: "ci_qty",       label: "CI Inv Qty",         width: 12, level: "variant", valueFn: (r) => r.ci_inventory_qty ?? "", align: "right" },
  { id: "order_qty",    label: "Order QTY",          width: 12, level: "variant", valueFn: (r) => r.qty ?? "", align: "right" },
  { id: "price",        label: "Price",              width: 14, level: "variant", isPrice: true, align: "right", bold: true },
];

const BORDER = { style: "thin", color: { argb: "FFD1D5DB" } };
const ALL_BORDERS = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };

/**
 * @param {Object} opts
 * @param {Array}  opts.rows      Variant-level rows as displayed (same product rows must be adjacent).
 * @param {String} opts.currency  "USD" | "CAD" — which suggested price to export.
 * @param {String} opts.filename  Optional filename prefix (default "products").
 */
export async function exportProductsExcel({ rows, currency = "USD", filename = "products" }) {
  if (!rows?.length) throw new Error("No rows to export");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "China Inventory";
  workbook.created = new Date();
  const sheet = workbook.addWorksheet("Products");
  sheet.columns = COLUMNS.map((c) => ({ key: c.id, width: c.width }));

  const priceField = currency === "CAD" ? "suggested_price_cad" : "suggested_price_usd";
  const priceNumFmt = currency === "CAD" ? '"C$"#,##0.00' : '"$"#,##0.00';

  const headerRow = sheet.getRow(1);
  headerRow.height = 22;
  COLUMNS.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.label;
    cell.font = { bold: true, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    cell.alignment = { vertical: "middle", horizontal: c.align || "left" };
    cell.border = ALL_BORDERS;
  });

  const groupSize = groupByProductId(rows);
  for (let idx = 0; idx < rows.length; idx++) {
    const r = rows[idx];
    const rowNum = idx + 2;
    sheet.getRow(rowNum).height = ROW_HEIGHT_PT;

    COLUMNS.forEach((c, i) => {
      const cell = sheet.getCell(rowNum, i + 1);
      cell.border = ALL_BORDERS;
      cell.font = { size: 11, bold: !!c.bold };
      cell.alignment = { vertical: "middle", horizontal: c.align || "left", wrapText: !!c.wrap };
      if (c.level === "product" && groupSize[idx] === 0) return; // merged into group start
      if (c.isPhoto) return;
      if (c.isPrice) {
        const v = r[priceField];
        cell.value = v != null ? Number(v) : "";
        if (v != null) cell.numFmt = priceNumFmt;
        return;
      }
      cell.value = c.valueFn(r);
      if (c.numFmt && cell.value !== "") cell.numFmt = c.numFmt;
    });

    const span = groupSize[idx];
    if (span > 0) {
      if (span > 1) {
        COLUMNS.forEach((c, i) => { if (c.level === "product") sheet.mergeCells(rowNum, i + 1, rowNum + span - 1, i + 1); });
      }
      // eslint-disable-next-line no-await-in-loop
      await embedPhoto({ workbook, sheet, photoUrl: r.photo_url, rowNum, col: 1, rowHeightPt: ROW_HEIGHT_PT, span, colWidthChars: PHOTO_COL_WIDTH });
    }
  }

  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
